// src/modules/panelGlobal/components/ClientePlataformaPanel.jsx
// Panel reutilizable: vehiculos SORT + plataforma de rastreo (Wialon/
// Leasecontrol/TRS Rastreo) con sus usuarios/grupos/unidades para un
// cod_servicio_cliente puntual. Usado por PanelGlobalDetailDialog (pestaña
// "General") y por HistorialCliente (boton "Plataforma").
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Chip, CircularProgress, Alert, Divider, Collapse,
  List, ListItem, ListItemText, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TableSortLabel, Autocomplete, TextField, Button,
  Paper, IconButton, Tooltip, Menu, MenuItem, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RateReviewIcon from "@mui/icons-material/RateReview";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import {
  getPanelGlobalDetalle,
  getPanelGlobalVehiculos,
  getPanelGlobalObservaciones,
  postPanelGlobalObservacion,
} from "../services/panelGlobalService";
import {
  getWialonUserDetalle,
  listWialonUsers,
  listWialonUnits,
  listWialonHwTypes,
  listWialonIconLibrary,
  listWialonUnitGroups,
  listWialonChipsDisponibles,
  updateWialonUser,
  updateWialonUnitGroup,
  updateWialonUnit,
  createWialonUser,
  createWialonUnitGroup,
  createWialonUnit,
  desvincularWialonUser,
} from "../../wialon/services/wialonUsers";
import {
  lcGetUsuarioDetalle,
  lcListUsuarios,
  lcListCuentas,
  lcListUnidades,
  lcListHwTypes,
  lcListIconLibrary,
  lcListUnitGroups,
  lcListChipsDisponibles,
  lcCreateUsuario,
  lcUpdateUsuario,
  lcUpdateUnitGroup,
  lcUpdateUnidad,
  lcCreateUnitGroup,
  lcCreateUnidad,
  lcDesvincularUsuario,
} from "../../leasecontrol/services/leasecontrolUsuarios.service";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import WialonUserEditDialog from "../../wialon/components/WialonUserEditDialog";
import LeasecontrolUsuarioDialog from "../../leasecontrol/components/LeasecontrolUsuarioDialog";
import GrupoFormDialog from "../../leasecontrol/components/GrupoFormDialog";
import UnidadFormDialog from "../../leasecontrol/components/UnidadFormDialog";
import AsignarGrupoDialog from "../../leasecontrol/components/AsignarGrupoDialog";
import TrsUsuarioFormDialog from "../../trsrastreo/components/TrsUsuarioFormDialog";
import TrsVehiculoFormDialog from "../../trsrastreo/components/TrsVehiculoFormDialog";
import TrsAsignarVehiculoDialog from "../../trsrastreo/components/TrsAsignarVehiculoDialog";
import TrsAsignarGrupoDialog from "../../trsrastreo/components/TrsAsignarGrupoDialog";
import { listarGrupos, listarVehiculos as listarVehiculosTrs, desvincularUsuarioVehiculo, editarGrupo as editarGrupoTrs } from "../../trsrastreo/services/trsRastreoService";
import { useAuth } from "../../../context/AuthContext";
import {
  normalizeTrsGrupoParaDialog,
  trsListUsuariosParaGrupo,
  trsListVehiculosParaGrupo,
  trsCrearGrupoParaDialog,
  trsEditarGrupoParaDialog,
} from "../../trsrastreo/utils/trsGrupoAdapters";

export const SectionTitle = ({ icon, children }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
    {icon}
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{children}</Typography>
  </Stack>
);

export const compactCellSx = { py: 0.3, fontSize: "0.78rem" };

// Mismo formateo que ya usan BusquedaPorIdentificador.jsx/ModemViewerDialog.jsx
// para el "lmsg.t" (unix timestamp del ultimo reporte) de Wialon/Leasecontrol.
const formatUnixDateTime = (ts) => {
  if (!ts && ts !== 0) return "-";
  const num = Number(ts);
  if (Number.isNaN(num)) return "-";
  const ms = num < 1e12 ? num * 1000 : num;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(ms));
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

// Fondo de cabecera de tabla que se adapta a tema claro/oscuro (mismo patron
// que headCellSx en PanelGlobalPage.jsx) — sin esto, en tema oscuro la
// cabecera queda casi del mismo color que el cuerpo y se pierde el contraste.
export const tableHeadCellSx = {
  ...compactCellSx,
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};

// Mismo color que el boton "Plataforma" de HistorialCliente, para que las
// cabeceras de grupo se vean consistentes con el boton que lleva a esta vista.
export const GRUPO_HEADER_COLOR = "#5c6bc0";
// Cabecera de un grupo que tiene al menos una unidad con placa SORT (mismo
// verde que ya se usa para resaltar esas filas en UnidadesPlataformaTable) —
// para distinguirlo de un vistazo aunque el grupo este colapsado.
export const GRUPO_HEADER_COLOR_SORT = "#2e7d32";
// Cabecera de la rama "sin grupo" (pseudo-grupo, no es un grupo real) — un
// gris distinto a los otros dos colores con significado (indigo = grupo
// normal, verde = tiene placa SORT), para que se distinga de un vistazo que
// no es un grupo de verdad. Mismo hex en claro/oscuro: el texto siempre es
// blanco fijo sobre esta cabecera, asi que no depende del tema.
export const GRUPO_HEADER_COLOR_SIN_GRUPO = "#64748b";

// Cuenta de Leasecontrol a la que pertenece un usuario (viene en su detalle). Se usa para ofrecer, al asignar un grupo, solo los de esa cuenta.
// Devuelve [] si el detalle todavía no la trae (el diálogo avisa en vez de mostrar grupos de otros clientes).
export const cuentasDeUsuario = (detalle) => (detalle?.cuenta?.id ? [{ id: detalle.cuenta.id, nombre: detalle.cuenta.nombre || null }] : []);

function VehiculosSortColumna({ codServicioCliente }) {
  const [vehiculos, setVehiculos] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPlacas, setTotalPlacas] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [centroEquipo, setCentroEquipo] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError("");
    getPanelGlobalVehiculos(codServicioCliente, { page: page + 1, limit: rowsPerPage })
      .then(({ data }) => {
        if (cancelado) return;
        setVehiculos(data.data || []);
        setTotal(data.total || 0);
        setTotalPlacas(data.totalPlacas || 0);
      })
      .catch(() => { if (!cancelado) setError("No se pudo cargar los vehículos."); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [codServicioCliente, page, rowsPerPage]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <DirectionsCarIcon fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Vehículos activos en SORT ({totalPlacas})
        </Typography>
        <Chip size="small" variant="outlined" label={`Módems: ${total}`} />
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={18} /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !vehiculos.length ? (
        <Typography variant="body2" color="text.secondary">Sin vehículos activos.</Typography>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Placa</TableCell>
                  <TableCell sx={tableHeadCellSx}>Serie</TableCell>
                  <TableCell sx={tableHeadCellSx}>Modelo</TableCell>
                  <TableCell sx={tableHeadCellSx}>Alias</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehiculos.map((v) => (
                  <TableRow key={v.num_sim}>
                    <TableCell sx={compactCellSx}>{v.placa || "—"}</TableCell>
                    <TableCell
                      onClick={() => setCentroEquipo(v.num_sim)}
                      sx={{
                        ...compactCellSx,
                        color: "#006d77",
                        fontWeight: 700,
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {v.num_sim}
                    </TableCell>
                    <TableCell sx={{ ...compactCellSx, whiteSpace: "nowrap" }}>{v.modelo || "—"}</TableCell>
                    <TableCell sx={compactCellSx}>{v.id_ref || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 0 } }}
          />
        </>
      )}

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo="modem"
        identificador={centroEquipo}
        onClose={() => setCentroEquipo(null)}
      />
    </Box>
  );
}

// Vincular un usuario existente de la plataforma al cliente: por ahora solo lo ve el rol admin.
export function EnlazarUsuarioForm({ plataforma, codServicioCliente, onVinculado }) {
  const { user } = useAuth();
  const [busqueda, setBusqueda] = useState("");
  const [opciones, setOpciones] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!busqueda.trim()) { setOpciones([]); return; }
    let cancelado = false;
    setBuscando(true);
    const t = setTimeout(() => {
      const listarUsuarios = plataforma === "wialon" ? listWialonUsers : lcListUsuarios;
      listarUsuarios({ search: busqueda, limit: 10 })
        .then(({ data }) => { if (!cancelado) setOpciones(data?.data || []); })
        .catch(() => { if (!cancelado) setOpciones([]); })
        .finally(() => { if (!cancelado) setBuscando(false); });
    }, 400);
    return () => { cancelado = true; clearTimeout(t); };
  }, [busqueda, plataforma]);

  const handleVincular = async () => {
    if (!seleccionado) return;
    setVinculando(true);
    setError("");
    try {
      if (plataforma === "wialon") {
        await updateWialonUser(seleccionado.id, { cod_servicio_cliente: codServicioCliente });
      } else {
        await lcUpdateUsuario({ userId: seleccionado.id, cod_servicio_cliente: codServicioCliente });
      }
      setSeleccionado(null);
      setBusqueda("");
      setOpciones([]);
      onVinculado();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo vincular el usuario.");
    } finally {
      setVinculando(false);
    }
  };


  if (user?.role !== "admin") return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          options={opciones}
          loading={buscando}
          value={seleccionado}
          onChange={(_, v) => setSeleccionado(v)}
          inputValue={busqueda}
          onInputChange={(_, v) => setBusqueda(v)}
          getOptionLabel={(o) => o?.name || o?.nm || ""}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText={busqueda.trim() ? "Sin resultados" : "Escribe para buscar…"}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              {o.name || o.nm}
              {o.local?.cod_servicio_cliente ? ` — ya vinculado a #${o.local.cod_servicio_cliente}` : ""}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Buscar usuario ${plataforma === "wialon" ? "Wialon" : "Leasecontrol"} para vincular…`}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {buscando && <CircularProgress size={14} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          size="small"
          variant="contained"
          startIcon={vinculando ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
          disabled={!seleccionado || vinculando}
          onClick={handleVincular}
          sx={{ whiteSpace: "nowrap" }}
        >
          Vincular
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

// Tabla de unidades de la plataforma (Wialon/Leasecontrol), resolviendo la
// placa por el `uid` de la unidad (que en la practica es el num_sim/serie del
// modem) contra el mapa de vehiculos de SORT — la fila se marca en verde
// cuando esa serie coincide con un vehiculo activo de SORT. Clic en la Serie
// abre el mismo dialog de detalle que usa Centro de equipos.
export function UnidadesPlataformaTable({ unidades, sortPlacaPorNumSim, onEditUnidad, showGrupo = false }) {
  const [centroEquipo, setCentroEquipo] = useState(null);

  if (!unidades.length) return <Typography variant="body2" color="text.secondary">Sin unidades.</Typography>;
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableHeadCellSx, width: 44 }} />
              <TableCell sx={tableHeadCellSx}>Placa (SORT)</TableCell>
              <TableCell sx={tableHeadCellSx}>Unidad</TableCell>
              <TableCell sx={tableHeadCellSx}>Serie</TableCell>
              <TableCell sx={tableHeadCellSx}>Última actualización</TableCell>
              {showGrupo && <TableCell sx={tableHeadCellSx}>Grupo</TableCell>}
              {onEditUnidad && <TableCell sx={{ ...tableHeadCellSx, width: 40 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {unidades.map((v) => {
              const placaSort = sortPlacaPorNumSim.get(String(v.uid));
              return (
                <TableRow key={v.id} sx={placaSort ? { bgcolor: (theme) => alpha(theme.palette.success.main, 0.15) } : undefined}>
                  <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>
                    {v.icono_url ? (
                      <Box
                        component="img"
                        src={v.icono_url}
                        alt={v.nm}
                        sx={{ width: 32, height: 32, objectFit: "contain", verticalAlign: "middle" }}
                      />
                    ) : (
                      <DirectionsCarIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                    )}
                  </TableCell>
                  <TableCell sx={compactCellSx}>{placaSort || "—"}</TableCell>
                  <TableCell sx={compactCellSx}>{v.nm}</TableCell>
                  <TableCell
                    onClick={() => setCentroEquipo(v.uid)}
                    sx={{
                      ...compactCellSx,
                      color: "#006d77",
                      fontWeight: 700,
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {v.uid}
                  </TableCell>
                  <TableCell sx={{ ...compactCellSx, whiteSpace: "nowrap" }}>{formatUnixDateTime(v.lmsg?.t)}</TableCell>
                  {showGrupo && (
                    <TableCell sx={compactCellSx}>
                      <Chip size="small" label={v._grupoNombre || "-"} sx={{ height: 20, fontSize: "0.68rem" }} />
                    </TableCell>
                  )}
                  {onEditUnidad && (
                    <TableCell sx={{ ...compactCellSx, textAlign: "center" }}>
                      <Tooltip title="Editar unidad">
                        <IconButton size="small" onClick={() => onEditUnidad(v)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo="modem"
        identificador={centroEquipo}
        onClose={() => setCentroEquipo(null)}
      />
    </>
  );
}

export function WialonLeasecontrolDetalle({ plataforma, externalUserIds, codServicioCliente, onDesvinculado, sortPlacaPorNumSim, readOnly = false }) {
  // Detalle completo (grupos + unidades sin grupo + password + etc.) por
  // usuario — antes se pedia TODO de una sola vez para cada usuario vinculado
  // (Promise.all sobre externalUserIds), lo que volvia la pagina muy pesada
  // de renderizar cuando hay muchos grupos/unidades. Ahora la lista inicial
  // es gratis (externalUserIds ya trae el username local, sin llamada a la
  // plataforma) y el arbol de un usuario puntual recien se pide y se
  // renderiza al hacer click sobre su tarjeta (acordeon, uno a la vez).
  const [detalleById, setDetalleById] = useState({});
  const [loadingIds, setLoadingIds] = useState(() => new Set());
  const [errorIds, setErrorIds] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  // Tabla de unidades de cada caja de Grupo — colapsada por defecto, igual
  // que en /wialon y /leasecontrol (PlataformaGruposPage.jsx): con grupos de
  // muchas unidades, renderizar todas las tablas de una vez (apenas se
  // expande el usuario) es lo que ponia pesada la pagina. Clave por
  // "usuarioId:ramaKey" porque el mismo grupo puede aparecer bajo distintos
  // usuarios vinculados.
  const [openGrupos, setOpenGrupos] = useState({});
  // Buscador de unidades dentro de un usuario expandido — filtra por nombre,
  // serie (UID) o placa SORT en TODOS sus grupos + unidades sin grupo a la
  // vez, sin depender de ir abriendo caja por caja. Clave por usuarioId.
  const [unidadSearchByUsuario, setUnidadSearchByUsuario] = useState({});
  const [desvinculandoId, setDesvinculandoId] = useState(null);
  // Menú de "más opciones" (⋮) de la tarjeta de Usuario — un solo Menu compartido,
  // posicionado según qué tarjeta lo abrió.
  const [menuAnchor, setMenuAnchor] = useState(null); // { el, usuarioId } | null
  // Diálogo de "Editar usuario" (nombre + contraseña) — reusa WialonUserEditDialog /
  // LeasecontrolUsuarioDialog tal cual, con el mismo objeto que ya usan sus propias
  // tablas (id/nm/fl + local), armado a partir de lo que ya trae el detalle de acá.
  const [editUser, setEditUser] = useState(null); // detalle del usuario | null
  const [editUserId, setEditUserId] = useState(null);
  // Diálogo de "Editar grupo" (nombre + unidades + usuarios) — reusa el mismo
  // editor completo de PlataformaGruposPage.jsx (GrupoFormDialog), sobre el
  // grupo tal cual viene en el árbol (mismo shape que listGroups).
  const [editGrupo, setEditGrupo] = useState(null); // grupo crudo | null
  const [editGrupoUsuarioId, setEditGrupoUsuarioId] = useState(null);
  // Diálogo de "Asignar grupo" — vincula el usuario a un grupo YA EXISTENTE
  // (a diferencia de "Registrar grupo", que crea uno nuevo).
  const [asignarGrupoUsuarioId, setAsignarGrupoUsuarioId] = useState(null);
  // Diálogo de "Editar unidad" (nombre + UID + hw type + icono + grupos),
  // reusa el mismo editor completo de PlataformaUnidadesPage.jsx
  // (UnidadFormDialog) sobre la unidad tal cual viene en la tabla del árbol.
  const [editUnidad, setEditUnidad] = useState(null); // unidad cruda | null
  const [editUnidadUsuarioId, setEditUnidadUsuarioId] = useState(null);
  // Menú "más opciones" (⋮) de cada caja de Grupo — mismo patrón que el de
  // Usuario, un solo Menu compartido posicionado según qué caja lo abrió.
  const [grupoMenuAnchor, setGrupoMenuAnchor] = useState(null); // { el, grupo, usuarioId } | null
  // "Registrar grupo" / "Registrar unidad" / "Registrar usuario": mismos
  // dialogos completos que ya usan /wialon y /leasecontrol, en mode="create"
  // — no estan atados a un usuario puntual del arbol, por eso van con su
  // propio estado simple (abierto/no). Un usuario nuevo no queda vinculado
  // automaticamente a este cliente (para eso ya esta "Vincular usuario" mas
  // arriba); esto solo crea la cuenta en la plataforma.
  const [createGrupoOpen, setCreateGrupoOpen] = useState(false);
  const [createUnidadOpen, setCreateUnidadOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);

  const fetchDetalleUsuario = (externalUserId) => {
    const fetchDetalle = plataforma === "wialon" ? getWialonUserDetalle : lcGetUsuarioDetalle;
    return fetchDetalle(externalUserId).then((r) => r.data);
  };

  // Tras guardar un diálogo de edición (usuario o grupo): recarga solo el
  // usuario afectado, sin tocar el resto de tarjetas ya cargadas/colapsadas.
  // Devuelve la promesa para que los diálogos de "Asignar ..." puedan esperarla
  // y mantener su spinner hasta que el árbol ya tenga el dato nuevo, en vez de
  // cerrarse antes y dejar al usuario mirando el árbol viejo unos instantes.
  const recargarUsuario = (externalUserId) => {
    if (!externalUserId) return Promise.resolve();
    return fetchDetalleUsuario(externalUserId)
      .then((data) => setDetalleById((prev) => ({ ...prev, [externalUserId]: data })))
      .catch(() => {});
  };

  const toggleUsuario = (externalUserId) => {
    if (expandedId === externalUserId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(externalUserId);
    if (detalleById[externalUserId] || loadingIds.has(externalUserId)) return;

    setLoadingIds((prev) => new Set(prev).add(externalUserId));
    setErrorIds((prev) => {
      if (!(externalUserId in prev)) return prev;
      const next = { ...prev };
      delete next[externalUserId];
      return next;
    });

    fetchDetalleUsuario(externalUserId)
      .then((data) => setDetalleById((prev) => ({ ...prev, [externalUserId]: data })))
      .catch(() => setErrorIds((prev) => ({ ...prev, [externalUserId]: "No se pudo cargar el detalle de este usuario." })))
      .finally(() => setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(externalUserId);
        return next;
      }));
  };

  const toggleGrupoRama = (usuarioId, ramaKey) => {
    const clave = `${usuarioId}:${ramaKey}`;
    setOpenGrupos((prev) => ({ ...prev, [clave]: !prev[clave] }));
  };

  const handleDesvincular = async (itemId) => {
    if (!window.confirm("¿Desvincular este usuario del cliente? El usuario NO se elimina de la plataforma, solo se quita la asociación.")) {
      return;
    }
    setDesvinculandoId(itemId);
    try {
      const desvincular = plataforma === "wialon" ? desvincularWialonUser : lcDesvincularUsuario;
      await desvincular(itemId, codServicioCliente);
      onDesvinculado?.();
    } catch (e) {
      window.alert(e?.response?.data?.error || "No se pudo desvincular el usuario.");
    } finally {
      setDesvinculandoId(null);
    }
  };

  return (
    <Stack spacing={1.5} sx={{ pb: 4 }}>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateGrupoOpen(true)}
          sx={{ bgcolor: GRUPO_HEADER_COLOR, color: "#fff", borderRadius: 0, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: GRUPO_HEADER_COLOR } }}
        >
          Registrar grupo
        </Button>
        {/* "Registrar unidad" oculto por el momento (a pedido) — se retoma
            despues; el dialogo/estado createUnidadOpen queda intacto para
            reactivarlo con solo volver a mostrar este boton. */}
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateUserOpen(true)}
          sx={{ bgcolor: GRUPO_HEADER_COLOR, color: "#fff", borderRadius: 0, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: GRUPO_HEADER_COLOR } }}
        >
          Registrar usuario
        </Button>
      </Stack>

      {!externalUserIds.length && <Alert severity="info">Sin usuarios vinculados.</Alert>}

      {externalUserIds.map((eu) => {
        const id = eu.external_user_id;
        const detalle = detalleById[id];
        const isExpanded = expandedId === id;
        const isLoading = loadingIds.has(id);
        const errMsg = errorIds[id];

        const grupos = detalle?.grupos || [];
        const directos = detalle?.vehiculos_directos_sin_grupo || [];

        // Las unidades sin grupo entran como una rama más del árbol (última), en vez de
        // una sección aparte debajo — mismo criterio visual que los grupos reales. Los
        // grupos SIN unidades tambien entran como rama (con "(0)"), en vez de esconderse
        // en una leyenda aparte — si no, justo despues de crear un grupo parecia que no
        // se habia enlazado.
        // tieneSort marca si alguna de sus unidades ya tiene placa SORT asociada (mismo
        // match que resalta esas filas en verde en UnidadesPlataformaTable), para que se
        // note aunque la caja del grupo este colapsada.
        const ramas = [
          ...grupos.map((g) => ({
            key: String(g.id),
            titulo: `${g.nm || g.name} (${g.unidades_detalle.length})`,
            unidades: g.unidades_detalle,
            grupoRaw: g,
            tieneSort: g.unidades_detalle.some((v) => sortPlacaPorNumSim.has(String(v.uid))),
          })),
          ...(directos.length ? [{
            key: "sin-grupo",
            titulo: `Unidades sin grupo (${directos.length})`,
            unidades: directos,
            tieneSort: directos.some((v) => sortPlacaPorNumSim.has(String(v.uid))),
          }] : []),
        ];

        // Buscador de unidades: filtra por nombre, serie (UID) o placa SORT
        // en todas las ramas a la vez, sin depender de ir abriendo caja por
        // caja — cada resultado queda etiquetado con su grupo de origen.
        const unidadSearch = (unidadSearchByUsuario[id] || "").trim().toLowerCase();
        const resultadosBusqueda = unidadSearch
          ? ramas.flatMap((r) =>
              r.unidades
                .filter((v) => {
                  const placaSort = sortPlacaPorNumSim.get(String(v.uid)) || "";
                  return [v.nm, v.uid, placaSort].some((s) => String(s || "").toLowerCase().includes(unidadSearch));
                })
                .map((v) => ({ ...v, _grupoNombre: r.titulo }))
            )
          : [];

        // Tarjeta de Usuario: avatar + nombre + conteo de grupos + contraseña (si está
        // registrada) + menú "⋮" (Editar usuario, Desvincular) + flecha de expandir.
        // Es clickeable entera: al hacer click se pide (una sola vez, se cachea) y se
        // despliega a la derecha el árbol de grupos/unidades de ESE usuario puntual —
        // el nombre sale gratis (external_user_ids ya trae el username local); el
        // resto recien aparece una vez cargado el detalle.
        const usuarioCard = (
          <Paper
            elevation={2}
            onClick={() => toggleUsuario(id)}
            sx={{
              borderRadius: 0,
              flexShrink: 0,
              minWidth: 220,
              cursor: "pointer",
              "&:hover": { bgcolor: (t) => t.palette.action.hover },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1, pr: 0.5, py: 0.75 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: (t) => t.palette.action.hover,
                  color: "text.secondary",
                }}
              >
                <PersonIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {detalle?.usuario?.nm || eu.external_username || "Usuario"}
                </Typography>
                {detalle && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", display: "block" }}>
                    Grupos ({grupos.length})
                  </Typography>
                )}
                {detalle?.local?.external_password && (
                  <Box sx={{ mt: 0.25 }} onClick={(e) => e.stopPropagation()}>
                    <TrsPasswordInline password={detalle.local.external_password} />
                  </Box>
                )}
                {detalle?.usuario_enabled === false && (
                  <Chip size="small" color="warning" label="Deshabilitado" sx={{ mt: 0.25, height: 18, fontSize: 10 }} />
                )}
              </Box>
              {isLoading && <CircularProgress size={16} />}
              {detalle && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, usuarioId: id }); }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleUsuario(id); }}>
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Paper>
        );

        return (
          <Box key={id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, boxShadow: 1, bgcolor: (t) => (t.palette.mode === "dark" ? "#2a2f45" : "#d1d6e6") }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", overflowX: "auto", mb: isExpanded ? 1 : 0 }}>
              {usuarioCard}

              {isExpanded && (
                errMsg ? (
                  <Alert severity="error" sx={{ ml: 3, flex: 1 }}>{errMsg}</Alert>
                ) : !detalle ? (
                  <Box sx={{ ml: 3, display: "flex", alignItems: "center", minHeight: 44 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : ramas.length > 0 ? (
                  <Box sx={{ ml: 3, flex: 1, minWidth: 260 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Buscar unidad por nombre, serie o placa..."
                      value={unidadSearchByUsuario[id] || ""}
                      onChange={(e) => setUnidadSearchByUsuario((prev) => ({ ...prev, [id]: e.target.value }))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1.25,
                        bgcolor: "#fff",
                        boxShadow: (theme) => theme.shadows[1],
                        borderRadius: 0,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "primary.main" },
                          "&.Mui-focused fieldset": { borderColor: "primary.main" },
                        },
                        "& .MuiInputBase-input": {
                          py: 1.1,
                          fontSize: "0.85rem",
                          color: "rgba(0, 0, 0, 0.87)",
                          "&::placeholder": { color: "rgba(0, 0, 0, 0.5)", opacity: 1 },
                        },
                      }}
                    />
                    {unidadSearch ? (
                      resultadosBusqueda.length > 0 ? (
                        <UnidadesPlataformaTable
                          unidades={resultadosBusqueda}
                          sortPlacaPorNumSim={sortPlacaPorNumSim}
                          onEditUnidad={(unidad) => { setEditUnidad(unidad); setEditUnidadUsuarioId(id); }}
                          showGrupo
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Sin resultados.</Typography>
                      )
                    ) : (
                  /* Árbol de conectores: un tronco corrido a lo largo de todos los grupos,
                     con una rama horizontal por caja — cada segmento vertical se arma en
                     porcentaje de SU PROPIA caja (0→rama y rama→100%), así no hace falta
                     medir alturas reales de las tablas. */
                  <Stack spacing={1.5}>
                    {ramas.map((r, idx) => (
                      <Box key={r.key} sx={{ position: "relative", pl: 3 }}>
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "21px",
                            width: "2px",
                            bgcolor: "divider",
                          }}
                        />
                        {idx < ramas.length - 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: "21px",
                              bottom: 0,
                              width: "2px",
                              bgcolor: "divider",
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: "21px",
                            width: "24px",
                            height: "2px",
                            bgcolor: "divider",
                          }}
                        />
                        <Paper variant="outlined" sx={{ borderRadius: 0, overflow: "hidden" }}>
                          <Box sx={{ bgcolor: r.key === "sin-grupo" ? GRUPO_HEADER_COLOR_SIN_GRUPO : (r.tieneSort ? GRUPO_HEADER_COLOR_SORT : GRUPO_HEADER_COLOR), px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#fff", flex: 1 }}>
                              {r.titulo}
                            </Typography>
                            {r.grupoRaw && (
                              <IconButton
                                size="small"
                                onClick={(e) => setGrupoMenuAnchor({ el: e.currentTarget, grupo: r.grupoRaw, usuarioId: id })}
                                sx={{ p: 0.25, color: "#fff" }}
                              >
                                <MoreVertIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => toggleGrupoRama(id, r.key)}
                              sx={{ p: 0.25, color: "#fff" }}
                            >
                              {openGrupos[`${id}:${r.key}`] ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Box>
                          <Collapse in={!!openGrupos[`${id}:${r.key}`]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 0.5 }}>
                              <UnidadesPlataformaTable
                                unidades={r.unidades}
                                sortPlacaPorNumSim={sortPlacaPorNumSim}
                                onEditUnidad={(unidad) => { setEditUnidad(unidad); setEditUnidadUsuarioId(id); }}
                              />
                            </Box>
                          </Collapse>
                        </Paper>
                      </Box>
                    ))}
                  </Stack>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>Sin grupos ni unidades.</Typography>
                )
              )}
            </Box>
          </Box>
        );
      })}

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            const usuarioId = menuAnchor?.usuarioId;
            setMenuAnchor(null);
            setEditUser(detalleById[usuarioId] || null);
            setEditUserId(usuarioId || null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar usuario
        </MenuItem>
        <MenuItem
          onClick={() => {
            const id = menuAnchor?.usuarioId;
            setMenuAnchor(null);
            setAsignarGrupoUsuarioId(id || null);
          }}
        >
          <GroupIcon fontSize="small" sx={{ mr: 1 }} /> Asignar grupo
        </MenuItem>
        {!readOnly && (
          <MenuItem
            disabled={desvinculandoId === menuAnchor?.usuarioId}
            onClick={() => {
              const id = menuAnchor?.usuarioId;
              setMenuAnchor(null);
              handleDesvincular(id);
            }}
          >
            {desvinculandoId === menuAnchor?.usuarioId
              ? <CircularProgress size={14} sx={{ mr: 1 }} />
              : <LinkOffIcon fontSize="small" sx={{ mr: 1 }} />}
            Desvincular
          </MenuItem>
        )}
      </Menu>

      <Menu anchorEl={grupoMenuAnchor?.el} open={!!grupoMenuAnchor} onClose={() => setGrupoMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            const grupo = grupoMenuAnchor?.grupo;
            const usuarioId = grupoMenuAnchor?.usuarioId;
            setGrupoMenuAnchor(null);
            setEditGrupo(grupo || null);
            setEditGrupoUsuarioId(usuarioId || null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar grupo
        </MenuItem>
      </Menu>

      {editUser && plataforma === "wialon" && (
        <WialonUserEditDialog
          open={!!editUser}
          onClose={() => setEditUser(null)}
          user={{ ...editUser.usuario, local: editUser.local }}
          vinculaciones={editUser.vinculaciones}
          onUpdated={() => {
            setEditUser(null);
            recargarUsuario(editUserId);
          }}
        />
      )}
      {editUser && plataforma !== "wialon" && (
        <LeasecontrolUsuarioDialog
          open={!!editUser}
          mode="edit"
          initialUser={{ ...editUser.usuario, local: editUser.local }}
          onClose={() => setEditUser(null)}
          onSuccess={() => {
            setEditUser(null);
            recargarUsuario(editUserId);
          }}
        />
      )}

      {asignarGrupoUsuarioId && (
        <AsignarGrupoDialog
          open={!!asignarGrupoUsuarioId}
          usuarioId={asignarGrupoUsuarioId}
          usuarioNombre={
            detalleById[asignarGrupoUsuarioId]?.usuario?.nm
            || externalUserIds.find((eu) => eu.external_user_id === asignarGrupoUsuarioId)?.external_username
          }
          listGroups={plataforma === "wialon" ? listWialonUnitGroups : lcListUnitGroups}
          updateGroup={plataforma === "wialon" ? updateWialonUnitGroup : lcUpdateUnitGroup}
          cuentas={plataforma === "wialon" ? undefined : cuentasDeUsuario(detalleById[asignarGrupoUsuarioId])}
          onClose={() => setAsignarGrupoUsuarioId(null)}
          onSaved={async () => {
            await recargarUsuario(asignarGrupoUsuarioId);
            setAsignarGrupoUsuarioId(null);
          }}
        />
      )}

      <GrupoFormDialog
        open={!!editGrupo}
        mode="edit"
        grupo={editGrupo}
        onClose={() => setEditGrupo(null)}
        onSaved={() => {
          setEditGrupo(null);
          recargarUsuario(editGrupoUsuarioId);
        }}
        updateGroup={plataforma === "wialon" ? updateWialonUnitGroup : lcUpdateUnitGroup}
        listUsers={plataforma === "wialon" ? listWialonUsers : lcListUsuarios}
        listUnits={plataforma === "wialon" ? listWialonUnits : lcListUnidades}
        providerColor={GRUPO_HEADER_COLOR}
      />

      <UnidadFormDialog
        open={!!editUnidad}
        unidad={editUnidad}
        onClose={() => setEditUnidad(null)}
        onSaved={() => {
          setEditUnidad(null);
          recargarUsuario(editUnidadUsuarioId);
        }}
        updateUnit={plataforma === "wialon" ? updateWialonUnit : lcUpdateUnidad}
        listUnits={plataforma === "wialon" ? listWialonUnits : lcListUnidades}
        listHwTypes={plataforma === "wialon" ? listWialonHwTypes : lcListHwTypes}
        listIconLibrary={plataforma === "wialon" ? listWialonIconLibrary : lcListIconLibrary}
        listUnitGroups={plataforma === "wialon" ? listWialonUnitGroups : lcListUnitGroups}
        providerColor={GRUPO_HEADER_COLOR}
      />

      <GrupoFormDialog
        open={createGrupoOpen}
        mode="create"
        onClose={() => setCreateGrupoOpen(false)}
        onSaved={() => {
          setCreateGrupoOpen(false);
          recargarUsuario(expandedId);
        }}
        createGroup={plataforma === "wialon" ? createWialonUnitGroup : lcCreateUnitGroup}
        listUsers={plataforma === "wialon" ? listWialonUsers : lcListUsuarios}
        listUnits={plataforma === "wialon" ? listWialonUnits : lcListUnidades}
        initialUserIds={expandedId ? [expandedId] : []}
        providerColor={GRUPO_HEADER_COLOR}
        listCuentas={plataforma === "wialon" ? undefined : lcListCuentas}
        codServicioCliente={codServicioCliente}
      />

      <UnidadFormDialog
        open={createUnidadOpen}
        mode="create"
        onClose={() => setCreateUnidadOpen(false)}
        onSaved={() => {
          setCreateUnidadOpen(false);
          recargarUsuario(expandedId);
        }}
        createUnit={plataforma === "wialon" ? createWialonUnit : lcCreateUnidad}
        listHwTypes={plataforma === "wialon" ? listWialonHwTypes : lcListHwTypes}
        listIconLibrary={plataforma === "wialon" ? listWialonIconLibrary : lcListIconLibrary}
        listUnitGroups={plataforma === "wialon" ? listWialonUnitGroups : lcListUnitGroups}
        listChips={plataforma === "wialon" ? listWialonChipsDisponibles : lcListChipsDisponibles}
        providerColor={GRUPO_HEADER_COLOR}
      />

      <LeasecontrolUsuarioDialog
        open={createUserOpen}
        mode="create"
        providerName={plataforma === "wialon" ? "Wialon" : "Leasecontrol"}
        createUsuario={plataforma === "wialon" ? createWialonUser : lcCreateUsuario}
        onClose={() => setCreateUserOpen(false)}
        onSuccess={() => setCreateUserOpen(false)}
        initialClienteServicio={codServicioCliente ? { cod_servicio_cliente: codServicioCliente } : undefined}
      />
    </Stack>
  );
}

// Misma logica que /trsrastreo/usuarios (mostrar/ocultar + copiar) para la
// contraseña en texto plano que ya expone rastreo_bo (usu_password_descr).
export function TrsPasswordInline({ password }) {
  const [visible, setVisible] = useState(false);
  if (!password) return null;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" component="span">
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
        {visible ? password : "•••••"}
      </Typography>
      <Tooltip title={visible ? "Ocultar" : "Mostrar"}>
        <IconButton size="small" onClick={() => setVisible((v) => !v)} sx={{ p: 0.15 }}>
          {visible ? <VisibilityOffIcon sx={{ fontSize: 13 }} /> : <VisibilityIcon sx={{ fontSize: 13 }} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Copiar">
        <IconButton size="small" onClick={() => navigator.clipboard?.writeText(password)} sx={{ p: 0.15 }}>
          <ContentCopyIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

const formatFechaTrs = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Tabla de vehiculos de TRS Rastreo — analoga a UnidadesPlataformaTable pero
// con los campos propios de rastreo_bo (veh_placa/veh_alias/veh_modid/
// veh_icono/device en vez de nm/uid/icono_url/lmsg), ya que la placa que
// muestra ES la placa real del vehiculo (no un match contra un num_sim como
// en Wialon).
export function TrsVehiculosTable({ vehiculos, sortPlacas, onEditVehiculo, onEliminarVehiculo, onDesvincular, showGrupo = false, maxHeight = 220 }) {
  const [centroEquipo, setCentroEquipo] = useState(null);
  const [mapaVehiculo, setMapaVehiculo] = useState(null);
  const [orden, setOrden] = useState({ campo: "placa", dir: "asc" });

  const filas = useMemo(() => {
    const signo = orden.dir === "asc" ? 1 : -1;
    const texto = (s) => String(s || "").trim().toLowerCase();
    const porPlaca = (a, b) => texto(a.veh_placa).localeCompare(texto(b.veh_placa), "es", { numeric: true });
    const sinGrupo = (v) => !v._grupoNombre || texto(v._grupoNombre) === "sin grupo";
    const comparar = {
      placa: porPlaca,
      alias: (a, b) => texto(a.veh_alias).localeCompare(texto(b.veh_alias), "es", { numeric: true }),
      modem: (a, b) => texto(a.veh_modid).localeCompare(texto(b.veh_modid), "es", { numeric: true }),
      fecha: (a, b) => (new Date(a.fecha_ultimo_registro || 0).getTime() || 0) - (new Date(b.fecha_ultimo_registro || 0).getTime() || 0),
      grupo: (a, b) => texto(a._grupoNombre).localeCompare(texto(b._grupoNombre), "es", { numeric: true }),
    }[orden.campo] || porPlaca;
    return [...vehiculos].sort((a, b) => {
      // "Sin grupo" siempre va al final, en cualquier dirección
      if (orden.campo === "grupo" && sinGrupo(a) !== sinGrupo(b)) return sinGrupo(a) ? 1 : -1;
      return signo * comparar(a, b) || porPlaca(a, b);
    });
  }, [vehiculos, orden]);

  if (!vehiculos.length) return <Typography variant="body2" color="text.secondary">Sin vehiculos.</Typography>;

  const tieneAcciones = !!(onEditVehiculo || onEliminarVehiculo || onDesvincular);
  const Encabezado = ({ campo, children }) => (
    <TableCell sx={tableHeadCellSx} sortDirection={orden.campo === campo ? orden.dir : false}>
      <TableSortLabel
        active={orden.campo === campo}
        direction={orden.campo === campo ? orden.dir : "asc"}
        onClick={() => setOrden((p) => ({ campo, dir: p.campo === campo && p.dir === "asc" ? "desc" : "asc" }))}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <TableContainer sx={{ maxHeight }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableHeadCellSx, width: 44 }} />
              <Encabezado campo="placa">Placa</Encabezado>
              <Encabezado campo="alias">Alias</Encabezado>
              <Encabezado campo="modem">Modem</Encabezado>
              <Encabezado campo="fecha">Última actualización</Encabezado>
              {showGrupo && <Encabezado campo="grupo">Grupo</Encabezado>}
              {tieneAcciones && <TableCell sx={{ ...tableHeadCellSx, textAlign: "center", whiteSpace: "nowrap" }}>Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((v) => {
              const enSort = sortPlacas?.has(String(v.veh_placa || "").trim().toUpperCase());
              return (
                <TableRow key={v.id || v.veh_modid} hover sx={enSort ? { bgcolor: (theme) => alpha(theme.palette.success.main, 0.15) } : undefined}>
                  <TableCell
                    onClick={() => setMapaVehiculo(v)}
                    sx={{ ...compactCellSx, textAlign: "center", cursor: "pointer" }}
                  >
                    <Tooltip title="Ver ubicación">
                      {v.veh_icono ? (
                        <Box component="img" src={v.veh_icono} alt={v.veh_placa} sx={{ width: 32, height: 32, objectFit: "contain", verticalAlign: "middle" }} />
                      ) : (
                        <DirectionsCarIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={compactCellSx}>{v.veh_placa}</TableCell>
                  <TableCell sx={compactCellSx}>{v.veh_alias}</TableCell>
                  <TableCell
                    onClick={() => setCentroEquipo(v.veh_modid)}
                    sx={{ ...compactCellSx, color: "#006d77", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                  >
                    {v.veh_modid || "-"}
                  </TableCell>
                  <TableCell sx={{ ...compactCellSx, whiteSpace: "nowrap" }}>
                    {v.device?.d_latitude != null && v.device?.d_longitude != null
                      && !(v.device.d_latitude === 0 && v.device.d_longitude === 0) ? (
                      <a
                        href={`https://www.google.com/maps?q=${v.device.d_latitude},${v.device.d_longitude}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#006d77", fontWeight: 700, textDecoration: "underline" }}
                      >
                        {formatFechaTrs(v.fecha_ultimo_registro)}
                      </a>
                    ) : (
                      formatFechaTrs(v.fecha_ultimo_registro)
                    )}
                  </TableCell>
                  {showGrupo && (
                    <TableCell sx={compactCellSx}>
                      <Chip size="small" label={v._grupoNombre || "-"} sx={{ height: 20, fontSize: "0.68rem" }} />
                    </TableCell>
                  )}
                  {tieneAcciones && (
                    <TableCell sx={{ ...compactCellSx, textAlign: "center", whiteSpace: "nowrap" }}>
                      {onEditVehiculo && (
                        <Tooltip title="Editar vehiculo">
                          <IconButton size="small" onClick={() => onEditVehiculo(v)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDesvincular && v._relacionId != null && (
                        <Tooltip title="Quitar vínculo con el usuario">
                          <IconButton size="small" onClick={() => onDesvincular(v)}>
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEliminarVehiculo && (
                        <Tooltip title="Eliminar vehiculo">
                          <IconButton size="small" color="error" onClick={() => onEliminarVehiculo(v)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo="modem"
        identificador={centroEquipo}
        onClose={() => setCentroEquipo(null)}
      />

      <Dialog open={!!mapaVehiculo} onClose={() => setMapaVehiculo(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={mapaVehiculo?.veh_icono || undefined}
              alt={mapaVehiculo?.veh_placa}
              variant="rounded"
              sx={{ width: 32, height: 32, bgcolor: "grey.200" }}
            >
              <DirectionsCarIcon sx={{ fontSize: 18, color: "grey.500" }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              Ubicación del vehículo
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {mapaVehiculo?.veh_placa} {mapaVehiculo?.veh_alias ? `· ${mapaVehiculo.veh_alias}` : ""} — {formatFechaTrs(mapaVehiculo?.fecha_ultimo_registro)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {mapaVehiculo?.device?.d_latitude != null && mapaVehiculo?.device?.d_longitude != null
            && !(mapaVehiculo.device.d_latitude === 0 && mapaVehiculo.device.d_longitude === 0) ? (
            <Box>
              <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip size="small" label={`Lat: ${mapaVehiculo.device.d_latitude}`} />
                <Chip size="small" label={`Lng: ${mapaVehiculo.device.d_longitude}`} />
                {mapaVehiculo.device.d_speed != null && (
                  <Chip size="small" label={`${mapaVehiculo.device.d_speed} km/h`} />
                )}
                <Button
                  size="small"
                  target="_blank"
                  rel="noreferrer"
                  href={`https://www.google.com/maps?q=${mapaVehiculo.device.d_latitude},${mapaVehiculo.device.d_longitude}`}
                >
                  Abrir en Google Maps
                </Button>
              </Box>
              <Box
                component="iframe"
                title="Mapa"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapaVehiculo.device.d_longitude - 0.01}%2C${mapaVehiculo.device.d_latitude - 0.01}%2C${mapaVehiculo.device.d_longitude + 0.01}%2C${mapaVehiculo.device.d_latitude + 0.01}&layer=mapnik&marker=${mapaVehiculo.device.d_latitude}%2C${mapaVehiculo.device.d_longitude}`}
                sx={{ width: "100%", height: 420, border: "1px solid #d1d5db", borderRadius: 1 }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Este vehículo no tiene coordenadas disponibles.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={() => setMapaVehiculo(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function TrsRastreoDetalle({ detalle, sortPlacas, codServicioCliente, onRefresh }) {
  const { usuarios = [] } = detalle;

  // Cuadrillas ("grupos") de TODO rastreo_bo, con sus vehiculos
  // (cuadrilla_relacion) y usuarios (usuarios_vinculados) ya embebidos —
  // se filtran por usuario en el render. A diferencia de Wialon, esto no
  // depende de un cache pesado, asi que se trae entero una sola vez al
  // abrir el panel.
  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(true);
  const [errorGrupos, setErrorGrupos] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [openGrupos, setOpenGrupos] = useState({});
  const [vehiculoSearchByUsuario, setVehiculoSearchByUsuario] = useState({});

  const [menuAnchor, setMenuAnchor] = useState(null); // { el, usuario } | null
  const [grupoMenuAnchor, setGrupoMenuAnchor] = useState(null); // { el, grupo } | null
  const [editUsuario, setEditUsuario] = useState(null); // usuario crudo | null
  const [editGrupo, setEditGrupo] = useState(null); // grupo (cuadrilla) crudo | null
  const [editVehiculo, setEditVehiculo] = useState(null); // vehiculo crudo | null
  const [asignarVehiculoUsuario, setAsignarVehiculoUsuario] = useState(null); // usuario crudo | null
  const [asignarGrupoUsuario, setAsignarGrupoUsuario] = useState(null); // usuario crudo | null
  const [createGrupoOpen, setCreateGrupoOpen] = useState(false);
  const [createUsuarioOpen, setCreateUsuarioOpen] = useState(false);
  const [createVehiculoOpen, setCreateVehiculoOpen] = useState(false);
  // Memorizado por referencia estable: GrupoFormDialog reinicializa su form
  // en un useEffect que depende de este objeto — sin memo, cada re-render de
  // este componente (ej. al tipear en el buscador de otro usuario) crearia
  // un objeto nuevo y le borraria al usuario lo que iba escribiendo.
  const grupoNormalizadoParaEditar = useMemo(() => normalizeTrsGrupoParaDialog(editGrupo), [editGrupo]);
  // cuadrilla_relacion[].info_relacionado (lo que trae listarGrupos) es un
  // resumen liviano sin veh_icono ni device (telemetria/ultima posicion) —
  // se complementa con el listado completo de vehiculos (id -> {veh_icono,
  // device}) para que el arbol muestre lo mismo que /trsrastreo/vehiculos.
  const [vehiculoInfoPorId, setVehiculoInfoPorId] = useState(new Map());

  const fetchGrupos = () => {
    setLoadingGrupos(true);
    setErrorGrupos("");
    return Promise.all([listarGrupos(), listarVehiculosTrs({ pageSize: 500 }).catch(() => null)])
      .then(([respGrupos, respVehiculos]) => {
        setGrupos(respGrupos?.data?.data || []);
        const vehiculosCompletos = respVehiculos?.data?.data || [];
        setVehiculoInfoPorId(new Map(
          vehiculosCompletos.map((v) => [String(v.id), { veh_icono: v.veh_icono, device: v.device, fecha_ultimo_registro: v.fecha_ultimo_registro }])
        ));
      })
      .catch(() => setErrorGrupos("No se pudieron cargar los grupos."))
      .finally(() => setLoadingGrupos(false));
  };

  useEffect(() => {
    fetchGrupos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tras crear/editar cualquier cosa: recarga los grupos (pueden haber cambiado
  // membresias) y le pide al padre que recargue detalle.usuarios/vehiculos.
  // Devuelve la promesa para que los dialogos de "Asignar ..." puedan esperarla
  // y mantener su spinner hasta que el arbol ya tenga el dato nuevo, en vez de
  // cerrarse antes y dejar al usuario mirando el arbol viejo unos instantes.
  const recargarTodo = () => Promise.all([fetchGrupos(), onRefresh?.() || Promise.resolve()]);

  const toggleUsuario = (id) => setExpandedId((prev) => (prev === id ? null : id));
  const toggleGrupoRama = (usuarioId, grupoId) => {
    const key = `${usuarioId}:${grupoId}`;
    setOpenGrupos((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Solo aplica a vehiculos "sin grupo" (vinculo directo usuario-vehiculo) —
  // los de una cuadrilla se quitan desde "Editar grupo", no desde aca.
  const handleDesvincularVehiculo = async (v) => {
    if (!v._relacionId) return;
    if (!window.confirm(`¿Quitar el vínculo directo con el vehículo ${v.veh_placa || v.veh_alias || v.id}?`)) return;
    try {
      await desvincularUsuarioVehiculo(v._relacionId);
      recargarTodo();
    } catch (e) {
      window.alert(e?.response?.data?.error || "No se pudo quitar el vínculo.");
    }
  };

  // Quita al usuario del grupo (no borra el grupo) — delta usuarios_remover,
  // mismo mecanismo que ya usa GrupoFormDialog/trsEditarGrupoParaDialog.
  const handleDesvincularGrupo = async (grupoId, usuarioId, grupoNombre) => {
    if (!grupoId || !usuarioId) return;
    if (!window.confirm(`¿Quitar a este usuario del grupo "${grupoNombre || grupoId}"?`)) return;
    try {
      await editarGrupoTrs(grupoId, { usuarios_remover: [Number(usuarioId)] });
      recargarTodo();
    } catch (e) {
      window.alert(e?.response?.data?.error || "No se pudo quitar del grupo.");
    }
  };

  if (loadingGrupos) return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>;
  if (errorGrupos) return <Alert severity="error">{errorGrupos}</Alert>;

  return (
    <Stack spacing={1.5} sx={{ pb: 15 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateGrupoOpen(true)}
          sx={{ bgcolor: GRUPO_HEADER_COLOR, color: "#fff", borderRadius: 0, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: GRUPO_HEADER_COLOR } }}
        >
          Registrar grupo
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateVehiculoOpen(true)}
          sx={{ bgcolor: GRUPO_HEADER_COLOR, color: "#fff", borderRadius: 0, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: GRUPO_HEADER_COLOR } }}
        >
          Registrar vehiculo
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateUsuarioOpen(true)}
          sx={{ bgcolor: GRUPO_HEADER_COLOR, color: "#fff", borderRadius: 0, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: GRUPO_HEADER_COLOR } }}
        >
          Registrar usuario
        </Button>
      </Stack>

      {!usuarios.length && <Alert severity="info">Sin usuarios vinculados.</Alert>}

      {usuarios.map((u) => {
        const gruposDelUsuario = grupos.filter((g) =>
          (g.usuarios_vinculados || []).some((uv) => String(uv.usuario_id) === String(u.id))
        );

        const vehiculosEnGruposIds = new Set(
          gruposDelUsuario.flatMap((g) => (g.cuadrilla_relacion || []).filter((r) => r.cuarel_activo).map((r) => String(r.for_id)))
        );
        const enriquecerVehiculo = (v) => {
          const extra = vehiculoInfoPorId.get(String(v.id));
          return {
            ...v,
            veh_icono: v.veh_icono || extra?.veh_icono || "",
            device: v.device || extra?.device || null,
            fecha_ultimo_registro: v.fecha_ultimo_registro || extra?.fecha_ultimo_registro || null,
          };
        };
        const directos = (u.vehiculos_relacionados || [])
          .filter((v) => !vehiculosEnGruposIds.has(String(v.id)))
          .map(enriquecerVehiculo);

        // Todos los grupos del usuario entran como rama, aunque no tengan
        // vehiculos todavia — antes los grupos vacios se escondian en una
        // leyenda aparte ("Sin vehiculos: ..."), lo que confundia justo
        // despues de crear uno (parecia que no se habia enlazado).
        const ramas = [
          ...gruposDelUsuario.map((g) => {
            const vehiculosRama = (g.cuadrilla_relacion || [])
              .filter((r) => r.cuarel_activo)
              .map((r) => enriquecerVehiculo({ ...r.info_relacionado, id: r.for_id }));
            return {
              key: String(g.id),
              titulo: `${g.cua_descripcion} (${vehiculosRama.length})`,
              vehiculos: vehiculosRama,
              grupoRaw: g,
              tieneSort: vehiculosRama.some((v) => sortPlacas?.has(String(v.veh_placa || "").trim().toUpperCase())),
            };
          }),
          ...(directos.length ? [{
            key: "sin-grupo",
            titulo: `Vehiculos sin grupo (${directos.length})`,
            vehiculos: directos,
            tieneSort: directos.some((v) => sortPlacas?.has(String(v.veh_placa || "").trim().toUpperCase())),
          }] : []),
        ];

        const isExpanded = expandedId === u.id;
        const search = (vehiculoSearchByUsuario[u.id] || "").trim().toLowerCase();
        const resultadosBusqueda = search
          ? ramas.flatMap((r) =>
              r.vehiculos
                .filter((v) => [v.veh_placa, v.veh_alias, v.veh_modid].some((s) => String(s || "").toLowerCase().includes(search)))
                .map((v) => ({ ...v, _grupoNombre: r.titulo }))
            )
          : [];

        return (
          <Box key={u.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, boxShadow: 1, bgcolor: (t) => (t.palette.mode === "dark" ? "#2a2f45" : "#d1d6e6") }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", overflowX: "auto", mb: isExpanded ? 1 : 0 }}>
              <Paper
                elevation={2}
                onClick={() => toggleUsuario(u.id)}
                sx={{
                  borderRadius: 0,
                  flexShrink: 0,
                  minWidth: 220,
                  cursor: "pointer",
                  "&:hover": { bgcolor: (t) => t.palette.action.hover },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1, pr: 0.5, py: 0.75 }}>
                  <Box
                    sx={{
                      width: 32, height: 32, borderRadius: 1, flexShrink: 0, display: "grid", placeItems: "center",
                      bgcolor: (t) => t.palette.action.hover, color: "text.secondary",
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {u.usu_username || u.usu_nombre || "Usuario"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", display: "block" }}>
                      Grupos ({gruposDelUsuario.length})
                    </Typography>
                    {u.usu_password_descr && (
                      <Box sx={{ mt: 0.25 }} onClick={(e) => e.stopPropagation()}>
                        <TrsPasswordInline password={u.usu_password_descr} />
                      </Box>
                    )}
                    {u.usu_activo === false && (
                      <Chip size="small" color="warning" label="Deshabilitado" sx={{ mt: 0.25, height: 18, fontSize: 10 }} />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, usuario: u }); }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleUsuario(u.id); }}>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </Paper>

              {isExpanded && (
                ramas.length > 0 ? (
                  <Box sx={{ ml: 3, flex: 1, minWidth: 260 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Buscar vehículo por placa, alias o modem..."
                      value={vehiculoSearchByUsuario[u.id] || ""}
                      onChange={(e) =>
                        setVehiculoSearchByUsuario((prev) => ({ ...prev, [u.id]: e.target.value }))
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1.25,
                        bgcolor: "#fff",
                        boxShadow: (theme) => theme.shadows[1], // Eleva el input directamente sin necesidad de Paper
                        borderRadius: 0,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "& fieldset": { borderColor: "transparent" }, // Elimina el doble borde gris
                          "&:hover fieldset": { borderColor: "primary.main" },
                          "&.Mui-focused fieldset": { borderColor: "primary.main" },
                        },
                        "& .MuiInputBase-input": {
                          py: 1.1,
                          fontSize: "0.85rem",
                          color: "rgba(0, 0, 0, 0.87)",
                          "&::placeholder": { color: "rgba(0, 0, 0, 0.5)", opacity: 1 },
                        },
                      }}
                    />

                    {search ? (
                      resultadosBusqueda.length > 0 ? (
                        <TrsVehiculosTable
                          vehiculos={resultadosBusqueda}
                          sortPlacas={sortPlacas}
                          onEditVehiculo={(v) => setEditVehiculo(v)}
                          onDesvincular={handleDesvincularVehiculo}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin resultados.
                        </Typography>
                      )
                    ) : (
                      <Stack spacing={1.5}>
                        {ramas.map((r, idx) => (
                          <Box key={r.key} sx={{ position: "relative", pl: 3 }}>
                            <Box
                              sx={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                height: "21px",
                                width: "2px",
                                bgcolor: "divider",
                              }}
                            />
                            {idx < ramas.length - 1 && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: "21px",
                                  bottom: 0,
                                  width: "2px",
                                  bgcolor: "divider",
                                }}
                              />
                            )}
                            <Box
                              sx={{
                                position: "absolute",
                                left: 0,
                                top: "21px",
                                width: "24px",
                                height: "2px",
                                bgcolor: "divider",
                              }}
                            />
                            
                            <Paper sx={{ borderRadius: 0 }}>
                              <Box
                                sx={{
                                  bgcolor: r.key === "sin-grupo" ? GRUPO_HEADER_COLOR_SIN_GRUPO : (r.tieneSort ? GRUPO_HEADER_COLOR_SORT : GRUPO_HEADER_COLOR),
                                  px: 1.5,
                                  py: 0.75,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#fff", flex: 1 }}>
                                  {r.titulo}
                                </Typography>
                                {r.grupoRaw && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      setGrupoMenuAnchor({ el: e.currentTarget, grupo: r.grupoRaw, usuarioId: u.id })
                                    }
                                    sx={{ p: 0.25, color: "#fff" }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => toggleGrupoRama(u.id, r.key)}
                                  sx={{ p: 0.25, color: "#fff" }}
                                >
                                  {openGrupos[`${u.id}:${r.key}`] ? (
                                    <ExpandLessIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              </Box>
                              <Collapse in={!!openGrupos[`${u.id}:${r.key}`]} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 0.5 }}>
                                  <TrsVehiculosTable
                                    vehiculos={r.vehiculos}
                                    sortPlacas={sortPlacas}
                                    onEditVehiculo={(v) => setEditVehiculo(v)}
                                    onDesvincular={!r.grupoRaw ? handleDesvincularVehiculo : undefined}
                                  />
                                </Box>
                              </Collapse>
                            </Paper>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>Sin grupos ni vehiculos.</Typography>
                )
              )}
            </Box>
          </Box>
        );
      })}

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            const usuario = menuAnchor?.usuario;
            setMenuAnchor(null);
            setEditUsuario(usuario || null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar usuario
        </MenuItem>
        <MenuItem
          onClick={() => {
            const usuario = menuAnchor?.usuario;
            setMenuAnchor(null);
            setAsignarVehiculoUsuario(usuario || null);
          }}
        >
          <DirectionsCarIcon fontSize="small" sx={{ mr: 1 }} /> Asignar vehiculo
        </MenuItem>
        <MenuItem
          onClick={() => {
            const usuario = menuAnchor?.usuario;
            setMenuAnchor(null);
            setAsignarGrupoUsuario(usuario || null);
          }}
        >
          <GroupIcon fontSize="small" sx={{ mr: 1 }} /> Asignar grupo
        </MenuItem>
      </Menu>

      <Menu anchorEl={grupoMenuAnchor?.el} open={!!grupoMenuAnchor} onClose={() => setGrupoMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            const grupo = grupoMenuAnchor?.grupo;
            setGrupoMenuAnchor(null);
            setEditGrupo(grupo || null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar grupo
        </MenuItem>
        <MenuItem
          onClick={() => {
            const { grupo, usuarioId } = grupoMenuAnchor || {};
            setGrupoMenuAnchor(null);
            handleDesvincularGrupo(grupo?.id, usuarioId, grupo?.cua_descripcion);
          }}
        >
          <LinkOffIcon fontSize="small" sx={{ mr: 1 }} /> Desvincular grupo
        </MenuItem>
      </Menu>

      {editUsuario && (
        <TrsUsuarioFormDialog
          open={!!editUsuario}
          mode="edit"
          usuario={editUsuario}
          onClose={() => setEditUsuario(null)}
          onSaved={() => { setEditUsuario(null); recargarTodo(); }}
        />
      )}

      {asignarVehiculoUsuario && (
        <TrsAsignarVehiculoDialog
          open={!!asignarVehiculoUsuario}
          usuario={asignarVehiculoUsuario}
          onClose={() => setAsignarVehiculoUsuario(null)}
          onSaved={async () => { await recargarTodo(); setAsignarVehiculoUsuario(null); }}
        />
      )}

      {asignarGrupoUsuario && (
        <TrsAsignarGrupoDialog
          open={!!asignarGrupoUsuario}
          usuario={asignarGrupoUsuario}
          grupos={grupos}
          onClose={() => setAsignarGrupoUsuario(null)}
          onSaved={async () => { await recargarTodo(); setAsignarGrupoUsuario(null); }}
        />
      )}

      {editVehiculo && (
        <TrsVehiculoFormDialog
          open={!!editVehiculo}
          mode="edit"
          vehiculo={editVehiculo}
          onClose={() => setEditVehiculo(null)}
          onSaved={() => { setEditVehiculo(null); recargarTodo(); }}
        />
      )}

      <GrupoFormDialog
        open={!!editGrupo}
        mode="edit"
        grupo={grupoNormalizadoParaEditar}
        onClose={() => setEditGrupo(null)}
        onSaved={() => { setEditGrupo(null); recargarTodo(); }}
        updateGroup={trsEditarGrupoParaDialog(editGrupo)}
        listUsers={trsListUsuariosParaGrupo}
        listUnits={trsListVehiculosParaGrupo}
        providerColor={GRUPO_HEADER_COLOR}
      />

      <GrupoFormDialog
        open={createGrupoOpen}
        mode="create"
        onClose={() => setCreateGrupoOpen(false)}
        onSaved={() => { setCreateGrupoOpen(false); recargarTodo(); }}
        createGroup={trsCrearGrupoParaDialog}
        listUsers={trsListUsuariosParaGrupo}
        listUnits={trsListVehiculosParaGrupo}
        providerColor={GRUPO_HEADER_COLOR}
        initialUserIds={expandedId ? [expandedId] : []}
      />

      <TrsVehiculoFormDialog
        open={createVehiculoOpen}
        mode="create"
        codServicioCliente={codServicioCliente}
        onClose={() => setCreateVehiculoOpen(false)}
        onSaved={() => { setCreateVehiculoOpen(false); recargarTodo(); }}
      />

      <TrsUsuarioFormDialog
        open={createUsuarioOpen}
        mode="create"
        codServicioCliente={codServicioCliente}
        onClose={() => setCreateUsuarioOpen(false)}
        onSaved={() => { setCreateUsuarioOpen(false); recargarTodo(); }}
      />
    </Stack>
  );
}

const REVISION_ESTADO_COLOR = { Concluido: "success", Pendiente: "warning" };

function ObservacionesSection({ codServicioCliente }) {
  const [observaciones, setObservaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState("");
  const [error, setError] = useState("");

  const fetchObservaciones = React.useCallback(() => {
    setLoading(true);
    getPanelGlobalObservaciones(codServicioCliente)
      .then(({ data }) => setObservaciones(data.data || []))
      .catch(() => setError("No se pudieron cargar las observaciones."))
      .finally(() => setLoading(false));
  }, [codServicioCliente]);

  useEffect(() => { fetchObservaciones(); }, [fetchObservaciones]);

  const handleGuardar = async (estado) => {
    if (!texto.trim()) {
      setError("Escribe una observación antes de guardar.");
      return;
    }
    setGuardando(estado);
    setError("");
    try {
      await postPanelGlobalObservacion(codServicioCliente, { observacion: texto.trim(), estado });
      setTexto("");
      fetchObservaciones();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo guardar la observación.");
    } finally {
      setGuardando("");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <SectionTitle icon={<RateReviewIcon fontSize="small" />}>Revisión del cliente</SectionTitle>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Observación"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!!guardando}
            onClick={() => handleGuardar("Concluido")}
          >
            {guardando === "Concluido" ? <CircularProgress size={16} color="inherit" /> : "Concluido"}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={!!guardando}
            onClick={() => handleGuardar("Pendiente")}
          >
            {guardando === "Pendiente" ? <CircularProgress size={16} color="inherit" /> : "Pendiente"}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={18} /></Box>
      ) : !observaciones.length ? (
        <Typography variant="body2" color="text.secondary">Sin observaciones registradas todavía.</Typography>
      ) : (
        <List dense sx={{ maxHeight: 220, overflowY: "auto" }}>
          {observaciones.map((o) => (
            <ListItem key={o.id} disableGutters alignItems="flex-start">
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color={REVISION_ESTADO_COLOR[o.estado] || "default"} label={o.estado} />
                    <Typography variant="caption" color="text.secondary">
                      {o.fecha?.slice(0, 10)} {o.hora} · {o.usu}
                    </Typography>
                  </Stack>
                }
                secondary={o.observacion}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

// ── Componente principal, reutilizable ──
export default function ClientePlataformaPanel({ codServicioCliente, showObservaciones = true }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [plataformaElegida, setPlataformaElegida] = useState(null);
  // Placas/series de SORT para este cliente, cargadas completas (no paginadas)
  // para poder marcar en verde las unidades de la plataforma que coinciden.
  const [sortPlacaPorNumSim, setSortPlacaPorNumSim] = useState(new Map());
  const [sortPlacas, setSortPlacas] = useState(new Set());

  const fetchDetalle = React.useCallback(() => {
    if (!codServicioCliente) { setDetalle(null); return; }
    setLoading(true);
    setError("");
    getPanelGlobalDetalle(codServicioCliente)
      .then(({ data }) => setDetalle(data))
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar el detalle."))
      .finally(() => setLoading(false));
  }, [codServicioCliente]);

  useEffect(() => {
    setPlataformaElegida(null);
    fetchDetalle();
    if (!codServicioCliente) {
      setSortPlacaPorNumSim(new Map());
      setSortPlacas(new Set());
      return;
    }
    getPanelGlobalVehiculos(codServicioCliente, { page: 1, limit: 200 })
      .then(({ data }) => {
        const items = data.data || [];
        // Un modem se puede identificar en Wialon/Leasecontrol por num_sim O por
        // emai (segun cual haya quedado cargado como "uid" de la unidad) — se
        // indexa por ambos para no perder la coincidencia de placa.
        const mapa = new Map();
        items.forEach((v) => {
          if (v.num_sim) mapa.set(String(v.num_sim), v.placa);
          if (v.emai) mapa.set(String(v.emai), v.placa);
        });
        setSortPlacaPorNumSim(mapa);
        setSortPlacas(new Set(items.map((v) => String(v.placa || "").trim().toUpperCase()).filter(Boolean)));
      })
      .catch(() => { setSortPlacaPorNumSim(new Map()); setSortPlacas(new Set()); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codServicioCliente]);

  if (!codServicioCliente) return null;

  return (
    <Box>
      {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && detalle && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <VehiculosSortColumna codServicioCliente={codServicioCliente} />

          <Box>
            {plataformaElegida && plataformaElegida !== "__picker__" ? (
              <>
                <Button size="small" onClick={() => setPlataformaElegida(null)} sx={{ mb: 1 }}>
                  ← Cancelar
                </Button>
                <EnlazarUsuarioForm
                  plataforma={plataformaElegida}
                  codServicioCliente={codServicioCliente}
                  onVinculado={() => { setPlataformaElegida(null); fetchDetalle(); }}
                />
              </>
            ) : plataformaElegida === "__picker__" || detalle.sin_plataforma || !detalle.plataforma ? (
              <Box>
                {plataformaElegida === "__picker__" && (
                  <Button size="small" onClick={() => setPlataformaElegida(null)} sx={{ mb: 1 }}>
                    ← Cancelar
                  </Button>
                )}
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {plataformaElegida === "__picker__"
                    ? "Elige a qué plataforma vincular un usuario para este cliente."
                    : "Este cliente no está emparejado con ninguna plataforma de rastreo."}
                </Alert>
                <Typography variant="body2" sx={{ mb: 1 }}>Elige una plataforma para vincular un usuario:</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => setPlataformaElegida("wialon")}>
                    Wialon
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setPlataformaElegida("leasecontrol")}>
                    Leasecontrol
                  </Button>
                </Stack>
              </Box>
            ) : detalle.plataforma === "trsrastreo" ? (
              <>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Button size="small" variant="text" onClick={() => setPlataformaElegida("__picker__")}>
                    Cambiar plataforma
                  </Button>
                </Stack>
                <TrsRastreoDetalle
                  detalle={detalle}
                  sortPlacas={sortPlacas}
                  codServicioCliente={codServicioCliente}
                  onRefresh={fetchDetalle}
                />
              </>
            ) : (
              <>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Button size="small" variant="text" onClick={() => setPlataformaElegida("__picker__")}>
                    Cambiar plataforma
                  </Button>
                </Stack>
                <EnlazarUsuarioForm
                  plataforma={detalle.plataforma}
                  codServicioCliente={codServicioCliente}
                  onVinculado={fetchDetalle}
                />
                <WialonLeasecontrolDetalle
                  plataforma={detalle.plataforma}
                  externalUserIds={detalle.external_user_ids || []}
                  codServicioCliente={codServicioCliente}
                  onDesvinculado={fetchDetalle}
                  sortPlacaPorNumSim={sortPlacaPorNumSim}
                />
              </>
            )}
          </Box>

          {showObservaciones && (
            <Box sx={{ gridColumn: { xs: "1", md: "1 / span 2" } }}>
              <Divider sx={{ mb: 2 }} />
              <ObservacionesSection codServicioCliente={codServicioCliente} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
